import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import HeaderSelectParcel from '../../components/v2/HeaderSelectParcel.js';
import { FooterSelectParcel } from '../../components/v2/FooterSelectParcel.js';
import { getRelayPoints } from '../../services/index.js';
import { useUser } from '../../contexts/UserContext.js';
import CircularProgress from '@mui/material/CircularProgress';
import { Typography, TextField, InputAdornment, IconButton, Box } from '@mui/material';
import backgroundSVG from './../../assets/logos/background-mob.svg';
import fallbackRelayPoints from '../../assets/data/mondialrelayv5.json';

const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
    </svg>
);

const LocationIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
);

// Fix default marker icon (bug connu react-leaflet avec webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Parser le champ ville v1 : "62000ARRAS" → { codePostal: "62000", ville: "ARRAS" }
function parseVille(villeStr) {
    if (!villeStr) return { codePostal: '', ville: '' };
    const cp = villeStr.substring(0, 5);
    const ville = villeStr.substring(5).trim();
    return { codePostal: cp, ville };
}

// Composant pour centrer la carte programmatiquement
function MapController({ center, zoom }) {
    const map = useMap();
    useEffect(() => {
        if (center) {
            map.setView(center, zoom || 12, { animate: true });
        }
    }, [center, zoom, map]);
    return null;
}

export function RelayMap() {
    const { clientId, jwt } = useUser();
    const [allPoints, setAllPoints] = useState([]);
    const [filteredPoints, setFilteredPoints] = useState([]);
    const [filterQuery, setFilterQuery] = useState('');
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [mapCenter, setMapCenter] = useState([46.603354, 1.888334]);
    const [mapZoom, setMapZoom] = useState(6);
    const [loading, setLoading] = useState(true);
    const [usingFallback, setUsingFallback] = useState(false);
    const markerRefs = useRef({});
    const cardRefs = useRef({});
    const listRef = useRef(null);

    const style = {
        container: {
            backgroundColor: '#F2F2F7',
            backgroundImage: `url(${backgroundSVG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'right 50% top -50vw',
            backgroundRepeat: 'no-repeat',
            minHeight: '100vh',
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
        },
        filterWrapper: {
            padding: '0.75rem 1rem',
            backgroundColor: 'white',
            borderBottom: '1px solid #E5E5EA',
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
        },
        mapWrapper: {
            height: '40vh',
            minHeight: '220px',
            flexShrink: 0,
        },
        map: {
            height: '100%',
            width: '100%',
        },
        listWrapper: {
            flex: 1,
            overflowY: 'auto',
            padding: '0.5rem 1rem',
            paddingBottom: '5rem',
        },
        listHeader: {
            padding: '0.5rem 0',
            color: '#666',
            fontSize: '0.8rem',
            fontWeight: 600,
        },
        card: (isSelected) => ({
            backgroundColor: isSelected ? '#000' : 'white',
            borderRadius: 12,
            padding: '0.75rem 1rem',
            marginBottom: '0.5rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem',
            boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.08)',
            transition: 'all 0.15s ease',
        }),
        cardIcon: (isSelected) => ({
            color: isSelected ? 'white' : '#000',
            marginTop: 2,
            flexShrink: 0,
        }),
        cardName: (isSelected) => ({
            fontWeight: 700,
            fontSize: '0.85rem',
            color: isSelected ? 'white' : '#000',
            lineHeight: 1.3,
        }),
        cardAddress: (isSelected) => ({
            fontSize: '0.78rem',
            color: isSelected ? 'rgba(255,255,255,0.75)' : '#666',
            marginTop: '0.15rem',
        }),
        fallbackBanner: {
            backgroundColor: '#FFF3CD',
            borderBottom: '1px solid #FFEAA7',
            padding: '0.4rem 1rem',
            fontSize: '0.75rem',
            color: '#856404',
            textAlign: 'center',
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '40vh',
            flexDirection: 'column',
            gap: '1rem',
        },
    };

    useEffect(() => {
        const fetchRelays = async () => {
            if (!clientId || !jwt) {
                setAllPoints(fallbackRelayPoints);
                setFilteredPoints(fallbackRelayPoints);
                setUsingFallback(true);
                setLoading(false);
                return;
            }
            try {
                const data = await getRelayPoints(clientId, jwt);
                const points = Array.isArray(data) ? data : (data.results || data.data || []);
                if (points.length > 0) {
                    setAllPoints(points);
                    setFilteredPoints(points);
                } else {
                    setAllPoints(fallbackRelayPoints);
                    setFilteredPoints(fallbackRelayPoints);
                    setUsingFallback(true);
                }
            } catch (err) {
                console.error('Erreur chargement points relais:', err);
                setAllPoints(fallbackRelayPoints);
                setFilteredPoints(fallbackRelayPoints);
                setUsingFallback(true);
            } finally {
                setLoading(false);
            }
        };
        fetchRelays();
    }, [clientId, jwt]);

    const handleFilter = useCallback(() => {
        const q = filterQuery.trim().toLowerCase();
        if (!q) {
            setFilteredPoints(allPoints);
            setMapCenter([46.603354, 1.888334]);
            setMapZoom(6);
            setSelectedPoint(null);
            return;
        }
        const filtered = allPoints.filter(p => {
            const { ville, codePostal } = parseVille(p.ville);
            return (
                ville.toLowerCase().includes(q) ||
                codePostal.includes(q) ||
                (p.nom || '').toLowerCase().includes(q)
            );
        });
        setFilteredPoints(filtered);
        setSelectedPoint(null);
        if (filtered.length > 0) {
            setMapCenter([parseFloat(filtered[0].lat), parseFloat(filtered[0].lng)]);
            setMapZoom(12);
        }
    }, [filterQuery, allPoints]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleFilter();
    };

    const handleSelectPoint = (point) => {
        setSelectedPoint(point);
        setMapCenter([parseFloat(point.lat), parseFloat(point.lng)]);
        setMapZoom(15);
        // Ouvrir le popup du marqueur
        setTimeout(() => {
            const marker = markerRefs.current[point.identifiant];
            if (marker) marker.openPopup();
        }, 300);
    };

    const handleMarkerClick = (point) => {
        setSelectedPoint(point);
        // Scroll vers la card correspondante
        const card = cardRefs.current[point.identifiant];
        if (card && listRef.current) {
            card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    };

    return (
        <div style={style.container}>
            <HeaderSelectParcel
                title={
                    <>
                        <span style={{ fontWeight: 400 }}>Trouver un</span>{' '}
                        <span style={{ fontWeight: 700 }}>point relais</span>
                    </>
                }
                subtitle="Carte des points relais"
            />

            {/* Barre de filtre */}
            <div style={style.filterWrapper}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Ville ou code postal..."
                    value={filterQuery}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <span style={{ color: '#999', display: 'flex' }}><SearchIcon /></span>
                            </InputAdornment>
                        ),
                        endAdornment: filterQuery && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => {
                                    setFilterQuery('');
                                    setFilteredPoints(allPoints);
                                    setMapCenter([46.603354, 1.888334]);
                                    setMapZoom(6);
                                    setSelectedPoint(null);
                                }}>
                                    ✕
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backgroundColor: '#F2F2F7',
                            '& fieldset': { borderColor: 'transparent' },
                            '&:hover fieldset': { borderColor: '#ccc' },
                            '&.Mui-focused fieldset': { borderColor: '#000' },
                        }
                    }}
                />
                <IconButton
                    onClick={handleFilter}
                    sx={{
                        backgroundColor: '#000',
                        color: 'white',
                        borderRadius: '10px',
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        '&:hover': { backgroundColor: '#333' },
                    }}
                >
                    <SearchIcon />
                </IconButton>
            </div>

            {usingFallback && (
                <div style={style.fallbackBanner}>
                    Affichage des points relais depuis le catalogue local
                </div>
            )}

            {/* Carte Leaflet */}
            <div style={style.mapWrapper}>
                {loading ? (
                    <div style={style.loadingContainer}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" style={{ color: '#666' }}>
                            Chargement...
                        </Typography>
                    </div>
                ) : (
                    <MapContainer
                        center={mapCenter}
                        zoom={mapZoom}
                        style={style.map}
                        scrollWheelZoom={true}
                        zoomControl={true}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        />
                        <MapController center={mapCenter} zoom={mapZoom} />
                        {filteredPoints.map((point) => {
                            const lat = parseFloat(point.lat);
                            const lng = parseFloat(point.lng);
                            if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;
                            const { codePostal, ville } = parseVille(point.ville);
                            const isSelected = selectedPoint?.identifiant === point.identifiant;
                            return (
                                <Marker
                                    key={point.identifiant}
                                    position={[lat, lng]}
                                    ref={(ref) => { if (ref) markerRefs.current[point.identifiant] = ref; }}
                                    eventHandlers={{
                                        click: () => handleMarkerClick(point),
                                    }}
                                    opacity={isSelected ? 1 : 0.75}
                                >
                                    <Popup>
                                        <div style={{ minWidth: 160 }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 2 }}>
                                                {point.nom}
                                            </div>
                                            <div style={{ fontSize: '0.78rem', color: '#555' }}>
                                                {codePostal} {ville}
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            );
                        })}
                    </MapContainer>
                )}
            </div>

            {/* Liste des points */}
            <div style={style.listWrapper} ref={listRef}>
                <div style={style.listHeader}>
                    {filteredPoints.length} point{filteredPoints.length !== 1 ? 's' : ''} relais
                    {filterQuery.trim() && ` pour "${filterQuery.trim()}"`}
                </div>
                {filteredPoints.map((point) => {
                    const { codePostal, ville } = parseVille(point.ville);
                    const isSelected = selectedPoint?.identifiant === point.identifiant;
                    return (
                        <div
                            key={point.identifiant}
                            ref={(ref) => { if (ref) cardRefs.current[point.identifiant] = ref; }}
                            style={style.card(isSelected)}
                            onClick={() => handleSelectPoint(point)}
                        >
                            <span style={style.cardIcon(isSelected)}><LocationIcon /></span>
                            <div style={{ minWidth: 0 }}>
                                <div style={style.cardName(isSelected)}>{point.nom}</div>
                                <div style={style.cardAddress(isSelected)}>
                                    {codePostal} {ville}
                                    {point.horaire && (
                                        <Box component="span" sx={{ display: 'block', fontSize: '0.72rem', mt: 0.3, opacity: 0.8 }}>
                                            {point.horaire}
                                        </Box>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {filteredPoints.length === 0 && !loading && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                        <Typography variant="body2">Aucun point relais trouvé</Typography>
                    </div>
                )}
            </div>

            <FooterSelectParcel text='Un service proposé par' />
        </div>
    );
}

export default RelayMap;
